/**
 * Access graph — `docs/SCREENS.md` §7.
 *
 * Laid out deterministically in three columns (identities → systems → data) rather than by a force
 * simulation. Two reasons, and the second matters more: a force layout moves every node when one
 * edge changes, so a reviewer cannot compare this week's picture with last week's; and a
 * deterministic layout is testable, which a settling simulation is not.
 *
 * Nodes are real buttons with `tabIndex`, so the graph is keyboard-operable. A visualisation that
 * can only be used with a mouse excludes exactly the auditors who are most likely to be reading it.
 */

import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { GraphEdge, GraphNode } from "@hub/core/browser";
import { useApi } from "../lib/api";
import { Card, KindBadge, Pill, Toggle } from "../components";

interface GraphShape {
  nodes: GraphNode[];
  edges: GraphEdge[];
  riskPathCount: number;
}

const COLUMN_X: Record<GraphNode["kind"], number> = { identity: 110, system: 400, data: 690 };
const ROW_H = 34;
const TOP = 30;

export function Graph() {
  const [params, setParams] = useSearchParams();
  const [sensitiveOnly, setSensitiveOnly] = useState(params.get("sensitiveOnly") === "1");
  const focus = params.get("focus");
  const [selected, setSelected] = useState<string | null>(focus);

  const query = `/v1/entitlements/graph?sensitiveOnly=${sensitiveOnly ? "true" : "false"}` +
                (focus ? `&focus=${encodeURIComponent(focus)}` : "");
  const { data, loading, error } = useApi<GraphShape>(query, [sensitiveOnly, focus]);

  const placed = useMemo(() => {
    const nodes = data?.nodes ?? [];
    const columns: Record<GraphNode["kind"], GraphNode[]> = { identity: [], system: [], data: [] };
    for (const n of nodes) columns[n.kind].push(n);
    // Capped per column: forty labelled rows is a picture, four hundred is a wall. The count of
    // what was left out is shown rather than silently dropped.
    const CAP = 14;
    const pos = new Map<string, { x: number; y: number; node: GraphNode }>();
    const omitted: Record<string, number> = {};
    for (const kind of ["identity", "system", "data"] as const) {
      const list = columns[kind];
      omitted[kind] = Math.max(0, list.length - CAP);
      list.slice(0, CAP).forEach((n, i) => {
        pos.set(n.id, { x: COLUMN_X[kind], y: TOP + i * ROW_H, node: n });
      });
    }
    return { pos, omitted };
  }, [data]);

  const edges = (data?.edges ?? []).filter(
    (e) => placed.pos.has(e.from) && placed.pos.has(e.to));

  const height = TOP + 15 * ROW_H;
  const selectedNode = selected ? placed.pos.get(selected)?.node : undefined;
  const selectedEdges = selected ? (data?.edges ?? []).filter((e) => e.from === selected || e.to === selected) : [];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Access graph</h1>
          <p className="sub">
            What can reach what. A dashed red path is a <strong>standing</strong> grant onto
            sensitive or financial data — for a person or an agent alike. A just-in-time grant onto
            the same data is not a risk path; it is the product working.
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <span style={{ fontSize: 12.5, color: "var(--muted)" }}>Sensitive data only</span>
          <Toggle on={sensitiveOnly} label="Sensitive data only"
                  onChange={(v) => { setSensitiveOnly(v); setParams(v ? { sensitiveOnly: "1" } : {}); }} />
        </div>
      </div>

      {error && <p className="err">{error.message}</p>}
      {loading && <p className="empty">Loading…</p>}

      <div className="grid" style={{ gridTemplateColumns: "minmax(0, 2fr) minmax(260px, 1fr)" }}>
        <Card title={`${data?.riskPathCount ?? 0} risk path${data?.riskPathCount === 1 ? "" : "s"}`}>
          <div className="table-wrap">
            <svg className="graph" viewBox={`0 0 820 ${height}`} style={{ minWidth: 640 }}
                 role="img" aria-label="Access graph: identities, systems and data classes">
              {edges.map((e) => {
                const a = placed.pos.get(e.from)!;
                const b = placed.pos.get(e.to)!;
                return (
                  <line key={e.id} className={`edge ${e.isRiskPath ? "risk" : ""}`}
                        x1={a.x + 70} y1={a.y} x2={b.x - 70} y2={b.y} />
                );
              })}
              {[...placed.pos.values()].map(({ x, y, node }) => (
                <g key={node.id} className="node" tabIndex={0} role="button"
                   aria-label={`${node.label}, ${node.kind}`}
                   data-testid="graph-node"
                   onClick={() => setSelected(node.id)}
                   onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelected(node.id); } }}>
                  {node.kind === "identity" && node.identityKind === "agent"
                    ? <circle cx={x - 78} cy={y} r={5} fill="var(--accent)" />
                    : node.kind === "identity"
                      ? <rect x={x - 83} y={y - 5} width={10} height={10} rx={2} fill="var(--dim)" />
                      : node.kind === "data"
                        ? <rect x={x - 83} y={y - 5} width={10} height={10} rx={2}
                                fill={node.dataTier === "financial" || node.dataTier === "sensitive" ? "var(--warn)" : "var(--muted)"} />
                        : <rect x={x - 83} y={y - 5} width={10} height={10} rx={2} fill="var(--muted)" />}
                  <text x={x - 66} y={y + 4} fontSize="11.5"
                        fill={selected === node.id ? "var(--text)" : "var(--muted)"}>
                    {node.label.length > 20 ? node.label.slice(0, 19) + "…" : node.label}
                  </text>
                </g>
              ))}
            </svg>
          </div>
          {Object.entries(placed.omitted).some(([, n]) => n > 0) && (
            <p className="sub" style={{ fontSize: 12, marginBottom: 0 }}>
              Showing the first 14 of each column.{" "}
              {Object.entries(placed.omitted).filter(([, n]) => n > 0)
                .map(([k, n]) => `${n} more ${k}`).join(" · ")}. Filter or focus to narrow it — the
              counts above the graph are over everything, not over what is drawn.
            </p>
          )}
        </Card>

        <Card title="Selection">
          {!selectedNode && <p className="empty">Select a node to see its effective access.</p>}
          {selectedNode && (
            <>
              <div style={{ marginBottom: 10 }}>
                <strong>{selectedNode.label}</strong>
                <div style={{ marginTop: 6 }}>
                  {selectedNode.identityKind
                    ? <KindBadge kind={selectedNode.identityKind} />
                    : <Pill tone={
                        selectedNode.dataTier === "financial" || selectedNode.dataTier === "sensitive"
                          ? "warn" : "dim"
                      }>{selectedNode.dataTier ?? selectedNode.kind}</Pill>}
                </div>
              </div>
              <div className="stack">
                {selectedEdges.length === 0 && <p className="empty">No edges in the current filter.</p>}
                {selectedEdges.slice(0, 25).map((e) => (
                  <div key={e.id} style={{ fontSize: 12.5, borderBottom: "1px solid var(--line-soft)", paddingBottom: 8 }}>
                    <div>
                      <strong>{e.permission}</strong>{" "}
                      {e.from === selected ? "→" : "←"}{" "}
                      {placed.pos.get(e.from === selected ? e.to : e.from)?.node.label ??
                       (e.from === selected ? e.to : e.from)}
                    </div>
                    <div style={{ color: "var(--muted)", marginTop: 2 }}>
                      granted by {e.source} ·{" "}
                      {e.isStanding ? <Pill tone={e.isRiskPath ? "bad" : "warn"}>standing</Pill>
                                    : <Pill tone="ok">just-in-time</Pill>}
                      {" "}· last certified {e.lastVerifiedAt ? new Date(e.lastVerifiedAt).toISOString().slice(0, 10) : "never"}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>
      </div>
    </>
  );
}
